import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SommelierService } from '@proxy/sommeliers';
import { finalize } from 'rxjs';
import { APP_ICONS } from '../../shared/icons/app-icons';
import { Location } from '@angular/common';
import { OrderService } from '@proxy/orders';
import { OrderDto } from '@proxy/orders/models';
import { OrderStatus } from '@proxy/enums/order-status.enum';
import { ShoppingCartService } from '@proxy/shopping-carts';

@Component({
  selector: 'app-client-details',
  templateUrl: './client-details.component.html',
  standalone: false
})
export class ClientDetailsComponent implements OnInit {
  userId: string;
  client: any = null;
  isLoading = true;
  isLoadingOrders = false;
  clientOrders: any[] = [];
  iconBack = APP_ICONS.arrow;
  iconCart = APP_ICONS.cart;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sommelierService: SommelierService,
    private location: Location,
    private orderService: OrderService,
    private shoppingCartService: ShoppingCartService
  ) {
    this.userId = this.route.snapshot.paramMap.get('userId') || '';
  }

  ngOnInit(): void {
    this.loadClientData();
  }

  loadClientData(): void {
    this.isLoading = true;
    this.sommelierService.getMyClients({})
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: (clients) => {
          this.client = clients.find(c => c.userId === this.userId);
          if (!this.client) {
            console.error('Cliente no encontrado');
          } else {
            this.loadClientOrders();
          }
        },
        error: (err) => console.error('Error al cargar cliente', err)
      });
  }

  loadClientOrders(): void {
    if (!this.userId) return;
    
    this.isLoadingOrders = true;
    
    // Primero intentamos cargar el carrito del cliente
    this.shoppingCartService.getCart(this.userId)
      .subscribe({
        next: (cart) => {
          // Luego cargamos las órdenes
          this.orderService.getClientOrders({ clientId: this.userId, maxResultCount: 50, skipCount: 0 })
            .pipe(finalize(() => this.isLoadingOrders = false))
            .subscribe({
              next: (result) => {
                let orders: any[] = result.items ? [...result.items] : [];
                
                // Si el carrito tiene items, creamos un pseudo-pedido y lo agregamos arriba
                if (cart && cart.items && cart.items.length > 0) {
                  const pseudoOrder = {
                    id: cart.cartId,
                    orderStatus: -1, // -1 representa "En carrito"
                    creationTime: cart.createdAt || new Date().toISOString(),
                    total: cart.subtotal || 0,
                    isCart: true
                  };
                  orders = [pseudoOrder, ...orders];
                }
                
                this.clientOrders = orders;
              },
              error: (err) => {
                console.error('Error al cargar órdenes del cliente', err);
                this.isLoadingOrders = false;
              }
            });
        },
        error: (err) => {
          console.error('Error al cargar carrito del cliente', err);
          // Si falla la carga del carrito, igual intentamos cargar las órdenes
          this.orderService.getClientOrders({ clientId: this.userId, maxResultCount: 50, skipCount: 0 })
            .pipe(finalize(() => this.isLoadingOrders = false))
            .subscribe({
              next: (result) => {
                this.clientOrders = result.items || [];
              },
              error: (err) => {
                console.error('Error al cargar órdenes del cliente', err);
                this.isLoadingOrders = false;
              }
            });
        }
      });
  }

  goBack(): void {
    this.location.back();
  }

  viewOrder(order: any): void {
    if (order.isCart) {
      this.manageCart();
    } else {
      this.router.navigate(['order', order.id], { relativeTo: this.route });
    }
  }

  manageCart(): void {
    this.router.navigate(['cart'], { relativeTo: this.route });
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount || 0);
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  getStatusClass(status: number): string {
    if (status === -1) {
      return 'bg-emerald-100 text-emerald-800'; // Estilo verde para "En carrito"
    }
    
    switch (status) {
      case OrderStatus.Pending:
        return 'bg-amber-100 text-amber-800'; // Estilo amarillo/naranja para "Validando pago"
      case OrderStatus.Paid:
        return 'bg-gray-100 text-gray-700'; // Estilo gris para "Pagado"
      case OrderStatus.Shipped:
        return 'bg-blue-50 text-blue-600'; 
      case OrderStatus.Delivered:
        return 'bg-blue-100 text-blue-800'; // Estilo azul para "Entregado"
      case OrderStatus.Cancelled:
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }

  getStatusLabel(status: number): string {
    switch (status) {
      case OrderStatus.Pending:
        return '::OrderPending';
      case OrderStatus.Paid:
        return '::Paid';
      case OrderStatus.Shipped:
        return '::Shipped';
      case OrderStatus.Delivered:
        return '::Delivered';
      case OrderStatus.Cancelled:
        return '::Cancelled';
      default:
        return '::Unknown';
    }
  }
}
